// src/Dashboard/pages/user/Inscription/components/RenewalDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBirthdayCake,
  FaCheck,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";

import DrawerShell from "./DrawerShell";

import {
  addAdherentGroups,
  updateAdherent,
} from "../../../../../services/adherentServices";
import { getActiveGroupsList } from "../../../../../services/groupServices";
import {
  getDossierInscription,
  updateDossierInscription,
} from "../../../../../services/dossierServices";

import {
  getAdherentAvatar,
  normalizeText,
} from "../utils/inscriptionUtils";

const DOSSIER_RENEWAL_RESET_PAYLOAD = {
  formulaire_licence_a_signer: null,
  formulaire_licence_signe: null,
  licence_url: null,
  validate_licence: false,
  validate_reglement: false,
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const getBirthYear = (adherent) => {
  const value =
    adherent?.date_naissance ||
    adherent?.dateNaissance ||
    adherent?.birth_date ||
    adherent?.birthDate;

  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    const match = String(value).match(/\d{4}/);
    return match ? match[0] : "";
  }

  return String(date.getFullYear());
};

const getSeasonStartYear = (nextSeason) => {
  const match = String(nextSeason || "").match(/\d{4}/);
  return match ? Number(match[0]) : new Date().getFullYear();
};

const getAgeForSeason = (birthYear, nextSeason) => {
  const year = Number(birthYear);
  const seasonStartYear = getSeasonStartYear(nextSeason);

  if (!Number.isInteger(year) || !Number.isInteger(seasonStartYear)) {
    return null;
  }

  return seasonStartYear - year;
};

const getGroupSearchText = (group) =>
  normalizeText(`${group?.nom || ""} ${group?.annees_naissance || ""}`);

const groupMatchesBirthYear = (group, birthYear) => {
  if (!birthYear) return false;

  const targetYear = Number(birthYear);
  const text = String(group?.annees_naissance || "");

  if (!text) return false;

  const years = text.match(/\d{4}/g)?.map(Number) || [];

  return years.includes(targetYear);
};

const groupMatchesAgePlus = (group, birthYear, nextSeason) => {
  const age = getAgeForSeason(birthYear, nextSeason);

  if (age === null) return false;

  const text = getGroupSearchText(group);

  if (!text) return false;

  const adultWords = [
    "adulte",
    "adultes",
    "senior",
    "seniors",
    "veteran",
    "veterans",
    "loisir adulte",
    "loisirs adultes",
  ];

  if (adultWords.some((word) => text.includes(word)) && age >= 18) {
    return true;
  }

  const patterns = [
    /\+\s*(\d{1,2})\s*ans?/i,
    /(\d{1,2})\s*ans?\s*(et\s*)?\+/i,
    /a\s*partir\s*de\s*(\d{1,2})\s*ans?/i,
    /des\s*(\d{1,2})\s*ans?/i,
    /dès\s*(\d{1,2})\s*ans?/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) continue;

    const minAge = Number(match[1]);

    if (Number.isInteger(minAge) && age >= minAge) {
      return true;
    }
  }

  return false;
};

const groupIsRecommended = (group, birthYear, nextSeason) => {
  return (
    groupMatchesBirthYear(group, birthYear) ||
    groupMatchesAgePlus(group, birthYear, nextSeason)
  );
};

function GroupChoiceCard({
  group,
  selected,
  recommended,
  alreadyLinked,
  onToggle,
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(group.id)}
      aria-pressed={selected}
      className={cn(
        "group relative w-full overflow-hidden rounded-[22px] border p-4 text-left transition",
        "focus:outline-none focus:ring-2 focus:ring-orange-500/40",
        selected
          ? "border-orange-400 bg-orange-50 shadow-sm dark:border-orange-400/60 dark:bg-orange-500/10"
          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-orange-300 hover:bg-slate-50 hover:shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:hover:border-orange-400/50 dark:hover:bg-slate-900"
      )}
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 w-1 transition",
          selected
            ? "bg-orange-600"
            : recommended
            ? "bg-emerald-500"
            : "bg-transparent"
        )}
      />

      <div className="flex items-start gap-3">
        <span
          className={cn(
            "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition",
            selected
              ? "border-orange-600 bg-orange-600 text-white"
              : "border-slate-300 bg-white text-transparent dark:border-slate-700 dark:bg-slate-900"
          )}
        >
          <FaCheck className="text-xs" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-slate-900 dark:text-white">
              {group?.nom || "Groupe sans nom"}
            </span>

            {recommended && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                conseillé
              </span>
            )}

            {alreadyLinked && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                déjà lié
              </span>
            )}
          </span>

          <span className="mt-1 block text-sm text-slate-500 dark:text-slate-400">
            Années / âge :{" "}
            <strong className="font-semibold text-slate-700 dark:text-slate-200">
              {group?.annees_naissance || "non renseigné"}
            </strong>
          </span>
        </span>
      </div>
    </button>
  );
}

export default function RenewalDrawer({
  isOpen,
  onClose,
  adherent,
  nextSeason,
  currentGroupNames = [],
}) {
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [filter, setFilter] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [saving, setSaving] = useState(false);

  const searchRef = useRef(null);

  const birthYear = useMemo(() => getBirthYear(adherent), [adherent]);

  const ageForSeason = useMemo(
    () => getAgeForSeason(birthYear, nextSeason),
    [birthYear, nextSeason]
  );

  const currentGroupNameSet = useMemo(() => {
    const names = Array.isArray(currentGroupNames) ? currentGroupNames : [];

    return new Set(names.map((name) => normalizeText(name)));
  }, [currentGroupNames]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadGroups = async () => {
      try {
        setLoadingGroups(true);

        const data = await getActiveGroupsList();

        if (!cancelled) {
          setGroups(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error(error);
        toast.error("Impossible de charger les groupes actifs.");
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };

    setSelectedGroupIds([]);
    setFilter("");

    loadGroups();

    setTimeout(() => searchRef.current?.focus(), 100);

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const visibleGroups = useMemo(() => {
    const q = normalizeText(filter);

    if (!q) return groups;

    return groups.filter((group) => getGroupSearchText(group).includes(q));
  }, [filter, groups]);

  const recommendedGroups = useMemo(() => {
    if (!birthYear) return [];

    return visibleGroups.filter((group) =>
      groupIsRecommended(group, birthYear, nextSeason)
    );
  }, [birthYear, nextSeason, visibleGroups]);

  const otherGroups = useMemo(() => {
    if (!birthYear) return visibleGroups;

    return visibleGroups.filter(
      (group) => !groupIsRecommended(group, birthYear, nextSeason)
    );
  }, [birthYear, nextSeason, visibleGroups]);

  const selectedGroups = useMemo(() => {
    const ids = new Set(selectedGroupIds.map(Number));

    return groups.filter((group) => ids.has(Number(group.id)));
  }, [groups, selectedGroupIds]);

  const selectedBirthYears = useMemo(() => {
    return selectedGroups
      .map((group) => group?.annees_naissance)
      .filter(Boolean)
      .join(" / ");
  }, [selectedGroups]);

  const selectedGroupNames = useMemo(() => {
    return selectedGroups
      .map((group) => group?.nom)
      .filter(Boolean)
      .join(", ");
  }, [selectedGroups]);

  const toggleGroup = (groupId) => {
    const id = Number(groupId);

    setSelectedGroupIds((previous) => {
      if (previous.includes(id)) {
        return previous.filter((currentId) => currentId !== id);
      }

      return [...previous, id];
    });
  };

  const getRenewalDossier = async () => {
    if (!adherent?.id) {
      throw new Error("Adhérent introuvable.");
    }

    try {
      const dossier = await getDossierInscription(adherent.id);

      if (!dossier?.id) {
        throw new Error("Dossier d'inscription introuvable.");
      }

      return dossier;
    } catch (error) {
      console.error(
        "[RenewalDrawer] Impossible de récupérer le dossier d'inscription:",
        error
      );

      throw error;
    }
  };

  const resetDossierInscriptionForRenewal = async (dossierId) => {
    if (!dossierId) {
      throw new Error("ID du dossier d'inscription introuvable.");
    }

    try {
      await updateDossierInscription(
        dossierId,
        DOSSIER_RENEWAL_RESET_PAYLOAD
      );
    } catch (error) {
      console.error(
        "[RenewalDrawer] Erreur lors de la réinitialisation du dossier d'inscription:",
        error
      );

      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!adherent?.id) {
      toast.error("Adhérent introuvable.");
      return;
    }

    if (!selectedGroupIds.length) {
      toast.warn("Veuillez sélectionner au moins un groupe.");
      return;
    }

    try {
      setSaving(true);

      const dossier = await getRenewalDossier();

      await updateAdherent(adherent.id, {
        saison_active: nextSeason,
        statut_inscription: "Inscrit",
      });

      await addAdherentGroups(adherent.id, selectedGroupIds);

      await resetDossierInscriptionForRenewal(dossier.id);

      toast.success(
        "Renouvellement enregistré. Le dossier d'inscription a été réinitialisé."
      );

      onClose?.(true, {
        adherentId: adherent.id,
        nextSeason,
        selectedGroupIds,
        selectedGroups,
        dossierId: dossier.id,
        dossierResetPayload: DOSSIER_RENEWAL_RESET_PAYLOAD,
      });
    } catch (error) {
      console.error(error);

      if (error?.response?.status === 404) {
        toast.error(
          "Aucun dossier d'inscription n'a été trouvé pour cet adhérent. Le renouvellement n'a pas été finalisé."
        );
      } else {
        toast.error(
          "Erreur lors de l’enregistrement du renouvellement ou de la réinitialisation du dossier."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onClose?.(false);
      }}
      title="Renouvellement d’adhésion"
      subtitle={`Saison ${nextSeason || "—"}`}
      icon={<FaRedoAlt />}
      width="max-w-3xl"
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => onClose?.(false)}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            form="renewal-form"
            disabled={saving || !selectedGroupIds.length}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Enregistrer le renouvellement"}
          </button>
        </div>
      }
    >
      {!adherent ? null : (
        <form id="renewal-form" onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-[24px] border border-orange-200 bg-orange-50 p-5 dark:border-orange-500/20 dark:bg-orange-500/10">
            <div className="flex items-center gap-4">
              <img
                src={getAdherentAvatar(adherent)}
                alt={`${adherent.prenom} ${adherent.nom}`}
                className="h-16 w-16 rounded-2xl object-cover ring-1 ring-orange-200 dark:ring-orange-500/20"
              />

              <div className="min-w-0">
                <h3 className="truncate text-lg font-bold text-slate-900 dark:text-white">
                  {(adherent.nom || "").toUpperCase()} {adherent.prenom || ""}
                </h3>

                <p className="mt-1 text-sm text-orange-700 dark:text-orange-200">
                  Saison actuelle : {adherent.saison_active || "—"} · Nouvelle
                  saison : {nextSeason || "—"}
                </p>

                <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-orange-800 ring-1 ring-orange-200 dark:bg-slate-950/40 dark:text-orange-100 dark:ring-orange-500/20">
                    <FaBirthdayCake />
                    Année : {birthYear || "inconnue"}
                  </span>

                  {ageForSeason !== null && (
                    <span className="inline-flex rounded-full bg-white/70 px-3 py-1 text-orange-800 ring-1 ring-orange-200 dark:bg-slate-950/40 dark:text-orange-100 dark:ring-orange-500/20">
                      Âge saison {nextSeason} : {ageForSeason} ans
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* <div className="rounded-[24px] border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-800 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-200">
            Le renouvellement mettra l’adhérent sur la saison active avec le
            statut <strong>Inscrit</strong>, l’ajoutera dans le ou les groupes
            sélectionnés, puis réinitialisera les éléments de licence et de
            règlement du dossier d’inscription.
          </div>

          <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            <p className="font-bold">Dossier d’inscription</p>

            <p className="mt-1">
              Le certificat médical, sa date, sa validation et le lien Google
              Drive seront conservés. Le formulaire de licence, la licence /
              assurance et les validations licence / règlement seront remis à
              zéro.
            </p>
          </div> */}

          <div>
            <label className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
              <FaSearch className="text-slate-400" />
              Filtrer les groupes
            </label>

            <input
              ref={searchRef}
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Nom du groupe, années de naissance ou âge..."
              className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>

          {loadingGroups ? (
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
              Chargement des groupes actifs...
            </div>
          ) : (
            <div className="space-y-5">
              {recommendedGroups.length > 0 && (
                <section>
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h4 className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                      <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                        <FaUsers />
                      </span>
                      Groupes conseillés
                    </h4>

                    <span className="inline-flex w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                      {recommendedGroups.length} groupe
                      {recommendedGroups.length > 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    {recommendedGroups.map((group) => (
                      <GroupChoiceCard
                        key={group.id}
                        group={group}
                        selected={selectedGroupIds.includes(Number(group.id))}
                        recommended
                        alreadyLinked={currentGroupNameSet.has(
                          normalizeText(group?.nom)
                        )}
                        onToggle={toggleGroup}
                      />
                    ))}
                  </div>
                </section>
              )}

              <section>
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h4 className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
                      <FaUsers />
                    </span>
                    {recommendedGroups.length > 0
                      ? "Autres groupes actifs"
                      : "Groupes actifs"}
                  </h4>

                  <span className="inline-flex w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                    {otherGroups.length} groupe
                    {otherGroups.length > 1 ? "s" : ""}
                  </span>
                </div>

                {otherGroups.length ? (
                  <div className="grid grid-cols-1 gap-3">
                    {otherGroups.map((group) => (
                      <GroupChoiceCard
                        key={group.id}
                        group={group}
                        selected={selectedGroupIds.includes(Number(group.id))}
                        recommended={false}
                        alreadyLinked={currentGroupNameSet.has(
                          normalizeText(group?.nom)
                        )}
                        onToggle={toggleGroup}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400">
                    Aucun autre groupe actif trouvé.
                  </div>
                )}
              </section>
            </div>
          )}

          {selectedGroupIds.length > 0 && (
            <div className="rounded-[24px] border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-100">
              <p>
                Groupe{selectedGroupIds.length > 1 ? "s" : ""} sélectionné
                {selectedGroupIds.length > 1 ? "s" : ""} :{" "}
                <span className="font-bold">{selectedGroupNames || "—"}</span>
              </p>

              {selectedBirthYears && (
                <p className="mt-1 text-xs text-orange-700 dark:text-orange-200">
                  Années / âge : {selectedBirthYears}
                </p>
              )}

              <button
                type="button"
                onClick={() => setSelectedGroupIds([])}
                disabled={saving}
                className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs font-bold text-orange-700 ring-1 ring-orange-200 transition hover:bg-orange-100 disabled:opacity-60 dark:bg-slate-950 dark:text-orange-200 dark:ring-orange-500/20 dark:hover:bg-slate-900"
              >
                <FaTimes />
                Vider la sélection
              </button>
            </div>
          )}
        </form>
      )}
    </DrawerShell>
  );
}